import {
  getRegionalDefaultConsent,
  isRegionalConsentEnabled,
} from "./regional-consent";

export const COOKIE_CONSENT_KEY = "rivetai_cookie_consent";

/**
 * - all: analytics and ad storage
 * - analytics: analytics only, ads denied ("Do not sell or share", or GPC)
 * - essential: neither
 */
export type CookieConsentValue = "all" | "analytics" | "essential";

/** The choice the visitor made, if any. Never an automatic default. */
export const getStoredCookieConsent = (): CookieConsentValue | null => {
  if (typeof window === "undefined") return null;
  const stored = window.localStorage.getItem(COOKIE_CONSENT_KEY);
  if (stored === "all" || stored === "essential") {
    return stored;
  }
  // Only region-based consent can store this choice.
  if (stored === "analytics" && isRegionalConsentEnabled()) {
    return stored;
  }
  return null;
};

/** The consent in effect: the stored choice, else the regional default. */
export const getCookieConsent = (): CookieConsentValue | null =>
  getStoredCookieConsent() ?? getRegionalDefaultConsent();

export const hasAnalyticsConsent = (): boolean => {
  const consent = getCookieConsent();
  return consent === "all" || consent === "analytics";
};

export const hasAdsConsent = (): boolean => getCookieConsent() === "all";

export const setCookieConsent = (value: CookieConsentValue): void => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(COOKIE_CONSENT_KEY, value);
  window.dispatchEvent(
    new CustomEvent("rivetai:cookie-consent", { detail: value }),
  );
};

/**
 * "Do not sell or share my personal information": turn ads off and keep
 * analytics only if the visitor already had it.
 */
export const denyAdsConsent = (): void => {
  setCookieConsent(hasAnalyticsConsent() ? "analytics" : "essential");
};
